import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  setError,
  setFulfilled,
  setPending,
  withRequestStatus,
} from '../../../../shared/store-utils/request-status.feature';
import { withErrorHandler } from '../../../../shared/store-utils/error-handler.feature';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, exhaustMap, pipe, tap } from 'rxjs';
import { ChangePasswordService } from '../services/change-password.service';
import { AuthStore } from '../../login/store/auth.store';
import { LoginService } from '../../login/services';
import { tapResponse } from '@ngrx/operators';
import { withRouter } from '../../../../shared/store-utils';
import { PASSWORD_CHANGED_MESSAGE } from '../../../../shared/constants/ui.constants';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { ForgotPasswordStore } from '../../forgot-password/store/forgot-password.store';
import { SuccessDialogData } from '../../../../shared/interfaces';
import { ChangePasswordComponent } from '../components/change-password.component';
import { PasswordValidators } from '../../../../shared/validators';
import { SharedUIService } from '../../../../shared/services';
import { HttpErrorResponse } from '@angular/common/http';

interface ChangePasswordState {
  confirmedPassword: boolean;
  forgotPassword: boolean;
}

const initialState: ChangePasswordState = {
  confirmedPassword: false,
  forgotPassword: false,
};

export const ChangePasswordStore = signalStore(
  withDevtools('ChangePasswordStore'),
  withState(initialState),
  withRequestStatus(),
  withErrorHandler(),
  withRouter(),
  withProps(() => ({
    _service: inject(ChangePasswordService),
    _authStore: inject(AuthStore),
    _loginService: inject(LoginService),
    _forgotStore: inject(ForgotPasswordStore),
    _sharedUIService: inject(SharedUIService),
    passwordForm: new FormBuilder().group({
      oldPassword: ['', Validators.required],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          new PasswordValidators().passwordStrengthValidator(),
        ],
      ],
      confirmNewPassword: [
        '',
        [
          Validators.required,
          new PasswordValidators().passwordMatchValidator('newPassword'),
        ],
      ],
    }),
    forgotPasswordForm: new FormBuilder().group({
      email: ['', [Validators.required, Validators.email]],
    }),
  })),

  withMethods((store) => {
    const confirmOldPassword = rxMethod<{
      oldPassword: string;
      control?: AbstractControl;
    }>(
      pipe(
        tap(() => patchState(store, setPending())),
        exhaustMap(({ oldPassword }) => {
          const email = store._authStore.currentUser()?.email;
          if (!email) {
            patchState(store, setError('User email not found.'));
            return EMPTY;
          }

          return store._loginService.login(email, oldPassword).pipe(
            tapResponse({
              next: () => {
                patchState(store, setFulfilled(), {
                  confirmedPassword: true,
                });
              },
              error: (error: HttpErrorResponse) => {
                patchState(
                  store,
                  setError(error.error.message || 'Change password failed'),
                );
              },
            }),
          );
        }),
      ),
    );

    const changePassword = rxMethod<{
      oldPassword: string;
      newPassword: string;
      dialogRef: MatDialogRef<ChangePasswordComponent>;
    }>(
      pipe(
        tap(() => patchState(store, setPending())),
        exhaustMap(({ oldPassword, newPassword, dialogRef }) =>
          store._service.changePassword(oldPassword, newPassword).pipe(
            tapResponse({
              next: () => {
                patchState(store, setFulfilled());
                store._sharedUIService.withCloseAction(
                  PASSWORD_CHANGED_MESSAGE,
                );
                closeDialog(dialogRef);
              },
              error: () => {
                patchState(store, setError('Password change failed'));
              },
            }),
          ),
        ),
      ),
    );

    function submitForgotPassword(dialogRef: MatDialogRef<SuccessDialogData>) {
      const control = store.forgotPasswordForm.get('email');

      store._forgotStore.forgotPassword({
        email: control?.value ?? '',
        dialogRef,
      });
    }

    const submitPasswordChange = rxMethod<
      MatDialogRef<ChangePasswordComponent>
    >(
      pipe(
        tap(() => patchState(store, setPending())),
        exhaustMap((dialogRef) => {
          const form = store.passwordForm;

          const { oldPassword, newPassword } = form.getRawValue();
          if (oldPassword === null || newPassword === null) {
            patchState(store, setError('Password fields cannot be empty.'));
            return EMPTY;
          }

          return store._service.changePassword(oldPassword, newPassword).pipe(
            tapResponse({
              next: () => {
                patchState(store, setFulfilled());
                store._sharedUIService.withCloseAction(
                  PASSWORD_CHANGED_MESSAGE,
                );
                dialogRef.close();
              },
              error: (error: HttpErrorResponse) => {
                patchState(
                  store,
                  setError(error.error.message || 'Password change failed'),
                );
              },
            }),
          );
        }),
      ),
    );

    function closeDialog(dialogRef: MatDialogRef<ChangePasswordComponent>) {
      dialogRef.close();
    }

    function enterForgotMode() {
      patchState(store, { forgotPassword: true });
    }

    function resetConfirmedPassword() {
      patchState(store, { confirmedPassword: false });
    }

    return {
      confirmOldPassword,
      changePassword,
      closeDialog,
      enterForgotMode,
      resetConfirmedPassword,
      submitForgotPassword,
      submitPasswordChange,
    };
  }),
);
